export interface QuoteTotals {
  subtotal: number;
  discount_amount: number;
  delivery_amount: number;
  service_charge_amount: number;
  gratuity_amount: number;
  tax_amount: number;
  total: number;
}

const nonNeg = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);
const toCents = (n: number) => Math.round(n * 100) / 100;

/**
 * A line's total the way Square charges it (#1062): the unit price rounded to
 * the cent, times the quantity. buildOrderBody sends each line as
 * Math.round(unit_price * 100) cents × quantity, so lines priced like this add
 * up to exactly what Square charges and a Full Amount link stays itemized.
 */
export function lineTotal(unitPrice: number, quantity: number): number {
  return (Math.round(unitPrice * 100) * quantity) / 100;
}

/**
 * Quote totals (#826). The discount comes off the goods/services subtotal only
 * and can't exceed it, so it never eats into delivery. Delivery is added after
 * the discount and is taxed (Ontario HST). A service charge only comes from a
 * Square sync (#828); it's carried through untaxed so editing a synced quote
 * keeps it in the total.
 *
 * A gratuity (#982) is a percentage of the discounted goods/services subtotal,
 * so it isn't charged on delivery. It sits outside the tax, like the service
 * charge, and is rounded to the cent so the figure on the quote is the figure
 * Square charges.
 *
 * Discount and delivery are typed by hand, so they're rounded to the cent too
 * (#861): 12.345 is stored as 12.35, the same 1235 cents the Square Delivery
 * line charges. The subtotal is rounded as well (#1062). New lines are already
 * cent-exact through lineTotal; this strips float noise from the sum and covers
 * older lines saved with an unrounded line_total.
 */
export function calcQuoteTotals(
  lineItems: ReadonlyArray<{ line_total: number }>,
  taxRate: number,
  discount: number,
  delivery = 0,
  serviceCharge = 0,
  gratuityPercent = 0,
): QuoteTotals {
  const subtotal = toCents(lineItems.reduce((s, li) => s + li.line_total, 0));
  const appliedDiscount = Math.min(toCents(nonNeg(discount)), nonNeg(subtotal));
  const appliedDelivery = toCents(nonNeg(delivery));
  const appliedServiceCharge = nonNeg(serviceCharge);
  const gratuity = toCents(nonNeg(subtotal - appliedDiscount) * (nonNeg(gratuityPercent) / 100));
  const taxable = subtotal - appliedDiscount + appliedDelivery;
  const taxAmount = taxable * (taxRate / 100);
  return {
    subtotal,
    discount_amount: appliedDiscount,
    delivery_amount: appliedDelivery,
    service_charge_amount: appliedServiceCharge,
    gratuity_amount: gratuity,
    tax_amount: taxAmount,
    total: taxable + taxAmount + appliedServiceCharge + gratuity,
  };
}
